import React, {Fragment, useState} from "react";
import {Modal} from "../../../react/components/ui/spectre/Modal";
import {ModalHeader} from "../../../react/components/ui/spectre/ModalHeader";
import {ModalBody} from "../../../react/components/ui/spectre/ModalBody";
import FacilityPath from "../FacilityPath";
import {Factory, FactoryGameType, IFacility, Satisfactory} from "../../types";
import Input from "../../../react/components/ui/spectre/Input";
import {Icon} from "../../../react/components/ui/spectre/icon";
import {getAllContainedFactories} from "../../Helper/FacilityAggregators";
import {getTranslator} from "../../../translate";
import {editFacility} from "../../Helper/EditFacility";
import {ModalFooter} from "../../../react/components/ui/spectre/ModalFooter";
import {Button, ButtonGroup, ButtonTypes} from "../../../react/components/ui/spectre/buttons";
import RadioButton from "../../../react/components/ui/spectre/RadioButton";

type Props = {
    facility: IFacility,
    close: () => void,
    reload: () => Promise<void>,
}

function makeItNice(recipeCount: number): number {
    const fraction = recipeCount - Math.floor(recipeCount);

    for (let i = 2; i <= 12; i++) {
        if (Math.abs(fraction * i - Math.round(fraction * i)) < 0.0003) {
            return Math.floor(recipeCount) + Math.round(fraction * i) / i;
        }
    }

    return recipeCount;
}

enum Strategy {
    Buildings,
    PowerShards,
    ClearShards,
}

function rescale(factory: Factory<Satisfactory>, factor: number, clockLimit: number): Partial<Factory<Satisfactory>> {
    const currentRecipeCount = factory.amount * factory.clockSpeed;
    const desiredRecipeCount = makeItNice(currentRecipeCount * factor);

    // new amount is minimum amount of buildings when clockLimit is the highest allowed clock speed
    // however if we scale *up* we don't demolish buildings
    const amount = Math.max(factor >= 1 ? factory.amount : 0, Math.ceil(desiredRecipeCount / clockLimit));
    const clockSpeed = desiredRecipeCount / amount;

    return { amount, clockSpeed, };
}

function rescaleStrategyBuildings(factory: Factory<Satisfactory>, factor: number): Partial<Factory<Satisfactory>> {
    return rescale(factory, factor, 2.5);
}

function rescaleStrategyPowerShards(factory: Factory<Satisfactory>, factor: number): Partial<Factory<Satisfactory>> {
    const maxAllowedShards = Math.ceil(Math.max(0.0, factory.clockSpeed - 1) * 2);
    return rescale(factory, factor, 1.0 + maxAllowedShards / 2);
}

function rescaleStrategyClearShards(factory: Factory<Satisfactory>, factor: number): Partial<Factory<Satisfactory>> {
    return rescale(factory, factor, 1.0);
}

function rescaleFunctionFactory(strategy: Strategy): (factory: Factory<Satisfactory>, factor: number) => Partial<Factory<Satisfactory>> {
    switch (strategy) {
        case Strategy.Buildings: return rescaleStrategyBuildings;
        case Strategy.PowerShards: return rescaleStrategyPowerShards;
        case Strategy.ClearShards: return rescaleStrategyClearShards;
    }
}

const translator = getTranslator('react/component/Satisfactory/FacilityRescaleModal');

export default function FacilityRescaleModal({ facility, reload, close }: Props) {
    if (facility.game !== FactoryGameType.Satisfactory) {
        return <div>Currently only supported for Satisfactory.</div>
    }

    const [from, setFrom] = useState<number>(1);
    const [to, setTo] = useState<number>(1);
    const [strategy, setStrategy] = useState<Strategy>(Strategy.Buildings);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const scaleFactor = (to || 1) / (from || 1);

    const factories: Array<Factory<Satisfactory>> = getAllContainedFactories(facility) as Array<Factory<Satisfactory>>;
    const suggestedDiff = factories.map(factory => {
        return {
            factory,
            patch: rescaleFunctionFactory(strategy)(factory, scaleFactor),
        }
    });

    async function savePatch() {
        setIsSaving(true);
        const promises = suggestedDiff.map(async ({ factory, patch }) => {
            await editFacility(factory, { ...factory, ...patch });
        });
        await Promise.all(promises);
        await reload();
        setIsSaving(false);
        close();
    }

    const isValid = from !== to;

    return <Modal onClose={() => close()}>
        <ModalHeader showCloseButton={true} title={translator('header')} onClose={() => close()}></ModalHeader>
        <ModalBody>
            <div>
                <FacilityPath facility={facility} excludeRoot={true} />
            </div>
            <div>
                {translator('explain')}
            </div>
            <div className='d-grid' style={{ gridTemplateColumns: "1fr auto 1fr", columnGap: ".4em", justifyContent: 'center', alignItems: 'center' }}>
                <Input
                    onChange={e => !isNaN(e.target.valueAsNumber) && setFrom(e.target.valueAsNumber)}
                    type="number"
                    value={from}
                    placeholder={translator('from')}
                />
                <Icon type='forward' />
                <Input
                    onChange={e => !isNaN(e.target.valueAsNumber) && setTo(e.target.valueAsNumber)}
                    type="number"
                    value={to}
                    placeholder={translator('to')}
                />
            </div>
            <div>
                <form onSubmit={e => e.preventDefault()}>
                    <RadioButton active={strategy === Strategy.Buildings} setActive={() => setStrategy(Strategy.Buildings)} label={translator('strategyBuildings')} />
                    <RadioButton active={strategy === Strategy.PowerShards} setActive={() => setStrategy(Strategy.PowerShards)} label={translator('strategyPowerShards')} />
                    <RadioButton active={strategy === Strategy.ClearShards} setActive={() => setStrategy(Strategy.ClearShards)} label={translator('strategyClearShards')} />
                </form>
            </div>
            <div className='my-2 text-bold'>
                {translator('validate')}
            </div>
            <div className='d-grid' style={{ gridTemplateColumns: "1fr 9em auto 9em", columnGap: '.4em' }}>
                {
                    suggestedDiff.map(({factory, patch}) => <Fragment key={factory.id}>
                        <div>{factory.name}</div>
                        <div>{factory.amount}x {factory.clockSpeed.toLocaleString('de-DE', { maximumFractionDigits: 4, style: 'percent' })}</div>
                        <div><Icon type='forward' /></div>
                        <div>{patch.amount}x {patch.clockSpeed.toLocaleString('de-DE', { maximumFractionDigits: 4, style: 'percent' })}</div>
                    </Fragment>)
                }
            </div>
        </ModalBody>
        <ModalFooter>
            <ButtonGroup>
                <Button onClick={() => savePatch()} buttonTypes={[ButtonTypes.success]} disabled={!isValid || isSaving}>{translator('save')}</Button>
            </ButtonGroup>
        </ModalFooter>
    </Modal>
}
